// mcpKeysApi — typed fetchers for /v1/me/api-keys (MCP read-only access).
//
// docs/specs/mcp-read-only.md §4.3 — user-managed API keys for external
// LLM clients (Claude Desktop / Cursor / etc.). Keys are bound to the
// caller's user_id + acting tenant at issue time, scoped to mcp:read,
// HMAC-hashed at rest. The raw_token is returned ONCE on create —
// thereafter the API only exposes the prefix.

import { apiFetch } from "../../lib/api-client.js";

export interface MyApiKey {
  id: string;
  label: string | null;
  /** First 12 chars of the raw key — "sntry_mcp_x" — display-only. */
  key_prefix: string;
  created_at: string;
  last_used_at: string | null;
  expires_at: string | null;
  status: "active" | "expired" | "revoked";
}

export interface IssuedApiKey {
  id: string;
  /** The full raw token. ONLY returned on create — store now or never. */
  raw_token: string;
  key_prefix: string;
  label: string | null;
  created_at: string;
  expires_at: string | null;
  scope: "mcp:read";
}

export class McpKeysApiError extends Error {
  status: number; code: string; correlationId: string | null; hint: string | null;
  constructor(status: number, code: string, hint: string | null, correlationId: string | null) {
    super(code);
    this.status = status; this.code = code; this.hint = hint; this.correlationId = correlationId;
  }
}

async function asError(r: Response): Promise<McpKeysApiError> {
  let code = "request_failed"; let hint: string | null = null;
  let cid: string | null = r.headers.get("x-correlation-id");
  try {
    const b = (await r.json()) as { error?: string; hint?: string; correlation_id?: string };
    code = b.error ?? code; hint = b.hint ?? null; cid = b.correlation_id ?? cid;
  } catch { /* not JSON */ }
  return new McpKeysApiError(r.status, code, hint, cid);
}

export async function listMyApiKeys(): Promise<MyApiKey[]> {
  const r = await apiFetch(`/v1/me/api-keys`, { credentials: "include" });
  if (!r.ok) throw await asError(r);
  const b = (await r.json()) as { items: MyApiKey[] };
  return b.items;
}

export interface CreateApiKeyInput {
  label?: string | null;
  /** ISO 8601 string or null (never expires). Backend rejects if delta
   *  < 1h or > ~3y from now. */
  expires_at?: string | null;
}

export async function createMyApiKey(input: CreateApiKeyInput): Promise<IssuedApiKey> {
  const r = await apiFetch(`/v1/me/api-keys`, {
    method: "POST",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({
      label: input.label ?? null,
      expires_at: input.expires_at ?? null,
    }),
  });
  if (!r.ok) throw await asError(r);
  const b = (await r.json()) as { ok: true; key: IssuedApiKey; warning: string };
  return b.key;
}

export async function revokeMyApiKey(id: string): Promise<void> {
  const r = await apiFetch(`/v1/me/api-keys/${encodeURIComponent(id)}`, {
    method: "DELETE",
    credentials: "include",
  });
  if (!r.ok) throw await asError(r);
}

export function friendlyMcpKeysError(e: unknown): string {
  if (!(e instanceof McpKeysApiError)) {
    return e instanceof Error ? e.message : "操作失敗，請稍後再試。";
  }
  switch (e.code) {
    case "key_limit_reached":
      return e.hint ?? "已達金鑰數量上限（10 把），請先撤銷一把再建立新的。";
    case "no_tenant_context":
      return "您目前沒有租戶身分；請先選擇要在哪個租戶下建立金鑰（super_admin 需先 impersonate）。";
    case "invalid_expires_at":
      return "到期時間必須介於 1 小時與 3 年之間。";
    case "mcp_key_hmac_not_configured":
      return "後端未設定 MCP HMAC 密鑰 — 請聯絡平台管理員。";
    case "missing_permission":
      return "您沒有管理 MCP 金鑰的權限。";
    case "key_not_found":
      return "金鑰不存在或已被撤銷。";
    default:
      return `${e.code}（${e.correlationId ?? "—"}）`;
  }
}
